import { useCallback } from 'react';

import { classifyCombinablePromotions } from '@/components/blocks/FreeGiftV2/utils';
import { GA4_EVENT_REMOVE_FROM_CART } from '@/data/constants/gtm';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { isB2BStore, useSettings } from '@/data/Settings';
import { ProductType } from '@/data/types/Product';
import { CombinablePromotion } from '@/data/types/Promotions';

import { triggerGA4EcommerceEvent } from 'presentation/events/services/ga4EcommerceService';
import { predictFreeGiftForRemoveFromCart } from 'presentation/events/utils/freeProductUtils';
import { buildGA4EcommerceEventPayload } from 'presentation/events/utils/ga4Utils';
import { useGetCategories } from 'shared/custom/hooks/Categories';
import { findParentCategories } from 'shared/custom/utils/getCategoryByIdForGA';

import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';

import { useCartSWRKey } from '@/data/Content/Cart';
import { Order } from '@/data/types/Order';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { useMobileApp } from '@/utils/useMobileApp';
import { B2B, B2C } from 'presentation/events/constants/eventConstants';
import {
	PromotionDetails,
	useGetPromotionDetails,
} from 'presentation/events/hooks/useGetPromotionDetails';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { getProductQuantity } from 'presentation/events/utils/productUtils';

import { unstable_serialize as serialize, useSWRConfig } from 'swr';

export type TrackRemoveFromCartProps = {
	product: ProductType;
	quantity: string | number;
	categories?: string[];
	cachedCartItems?: Order['orderItem'];
};

type SendEventProps = {
	product: ProductType & {
		isGift?: boolean;
		freeQty?: string | undefined;
		promoThdCode?: string;
	};
	quantity: string | number;
	categories: string[];
	promotionDetails: PromotionDetails | undefined;
};

export const useTrackRemoveFromCart = () => {
	const trackException = useTrackException();
	const getPromotionDetails = useGetPromotionDetails();
	const { navigation } = useGetCategories();
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { isMobileApp } = useMobileApp();
	const isB2B = isB2BStore(settings);
	const cartSWRKey = useCartSWRKey();
	const { cache } = useSWRConfig();

	/**
	 * Sends Google Analytics (GA4) event for Remove from Cart
	 */
	const sendGA4Event = useCallback(
		({ product, categories, quantity, promotionDetails }: SendEventProps) => {
			const { ga4 } = getGTMConfig(settings);

			if (!ga4 || !physicalStoreId) return;

			const payload = buildGA4EcommerceEventPayload({
				event: GA4_EVENT_REMOVE_FROM_CART,
				settings,
				product,
				categories,
				physicalStoreId,
				quantity,
				promotionDetails,
			});

			if (!isMobileApp) {
				triggerGA4EcommerceEvent(payload, { cleanup: true });
				return;
			}

			const mobilePayload = {
				...payload.ecommerce,
				app_thd: !isB2B ? B2C : B2B,
			};

			logEventWithValues(payload.event, mobilePayload);
		},
		[isB2B, isMobileApp, physicalStoreId, settings]
	);

	return useCallback(
		// eslint-disable-next-line complexity
		async ({ product, quantity, cachedCartItems, ...props }: TrackRemoveFromCartProps) => {
			try {
				const promotionDetails = await getPromotionDetails({
					catentryId: product.id,
				});

				const categories =
					props.categories ?? findParentCategories(navigation, product.parentCatalogGroupID);

				const cartData = cache.get(serialize(cartSWRKey))?.data as Order;
				const cartItems = cachedCartItems ?? cartData?.orderItem ?? [];

				const ownPromoThdCode = classifyCombinablePromotions(
					promotionDetails?.combinablePromotions as CombinablePromotion[] | undefined
				);

				let trackedProduct: SendEventProps['product'] = ownPromoThdCode
					? { ...product, promoThdCode: ownPromoThdCode }
					: product;

				if (physicalStoreId && cartItems.length > 0) {
					const cartItemsPromotionDetails = new Map<string, PromotionDetails>();

					const results = await Promise.all(
						cartItems.map(async (item) => {
							const details = await getPromotionDetails({ catentryId: item.productId });
							return { productId: item.productId, details };
						})
					);
					results.forEach(({ productId, details }) => {
						if (details) {
							cartItemsPromotionDetails.set(productId, details);
						}
					});

					if (promotionDetails) {
						cartItemsPromotionDetails.set(product.id, promotionDetails);
					}

					if (cartItemsPromotionDetails.size > 0) {
						const prediction = predictFreeGiftForRemoveFromCart({
							productCatentryId: product.id,
							removeQuantity: getProductQuantity({ quantity }),
							cartItems: cartItems.map((item) => ({
								partNumber: item.partNumber,
								productId: item.productId,
								quantity: item.quantity,
							})),
							cartItemsPromotionDetails,
							physicalStoreId,
						});

						if (prediction.isGift) {
							trackedProduct = {
								...product,
								isGift: true,
								freeQty: prediction.freeQty,
								promoThdCode: prediction.promoThdCode ?? ownPromoThdCode,
							};
						}
					}
				}

				sendGA4Event({
					product: trackedProduct,
					categories,
					quantity,
					promotionDetails: promotionDetails ?? undefined,
				});
			} catch (e) {
				console.error('Error sending remove from cart event', e);
				trackException(e);
			}
		},
		[
			cache,
			cartSWRKey,
			physicalStoreId,
			getPromotionDetails,
			navigation,
			sendGA4Event,
			trackException,
		]
	);
};
